/**
 * MinijuegosHub.js — Punto de entrada del sistema de minijuegos.
 */
import React from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import Formidable from './formidable/Formidable';
import PenalesGame from './penales/PenalesGame';
import FormacionesMiticas from './formacionesMiticas/FormacionesMiticas';

const MINIJUEGOS = [
  {
    id:          'formaciones',
    titulo:      'Formaciones Míticas',
    descripcion: 'Completá el once titular de equipos históricos del fútbol. Tenés 3 minutos.',
    emoji:       '',
    color:       '#f59e0b',
    ruta:        'formaciones',
    componente:  FormacionesMiticas,
  },
  {
    id:          'formidable',
    titulo:      'Formidable',
    descripcion: 'Adiviná al jugador histórico del fútbol argentino haciendo preguntas estratégicas.',
    emoji:       '',
    color:       '#a855f7',
    ruta:        'formidable',
    componente:  Formidable,
  },
  {
    id:          'penales',
    titulo:      'Penales',
    descripcion: 'Enfrentate al arquero. Elegí la zona y anotá. La dificultad aumenta con tu racha.',
    emoji:       '',
    color:       '#00f2ff',
    ruta:        'penales',
    componente:  PenalesGame,
  },
];

// ── Tarjeta ───────────────────────────────────────────────────────────────────
const TarjetaMinijuego = ({ juego }) => {
  const navigate = useNavigate();
  return (
    <div
      onClick={() => navigate(`/minijuegos/${juego.ruta}`)}
      style={{
        background: 'linear-gradient(135deg, #080d14 0%, #0f172a 100%)',
        border: `1px solid ${juego.color}33`,
        borderRadius: '16px',
        padding: '28px 24px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        position: 'relative',
        overflow: 'hidden',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = `${juego.color}88`;
        e.currentTarget.style.transform   = 'translateY(-4px)';
        e.currentTarget.style.boxShadow   = `0 8px 32px ${juego.color}22`;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = `${juego.color}33`;
        e.currentTarget.style.transform   = 'translateY(0)';
        e.currentTarget.style.boxShadow   = 'none';
      }}
    >
      <div style={{
        position: 'absolute', top: '-30px', right: '-30px',
        width: '120px', height: '120px', borderRadius: '50%',
        background: `radial-gradient(circle, ${juego.color}15 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />
      <div style={{ fontSize: '2.4rem' }}>{juego.emoji}</div>
      <div>
        <h3 style={{ color: juego.color, margin: '0 0 6px', fontSize: '1.2rem', letterSpacing: '1px' }}>
          {juego.titulo}
        </h3>
        <p style={{ color: '#64748b', fontSize: '0.85rem', margin: 0, lineHeight: 1.5 }}>
          {juego.descripcion}
        </p>
      </div>
      <span style={{
        color: juego.color, fontSize: '0.75rem', fontWeight: 'bold',
        alignSelf: 'flex-start', marginTop: 'auto',
        background: `${juego.color}15`, padding: '4px 12px',
        borderRadius: '20px', border: `1px solid ${juego.color}33`,
      }}>
        Jugar →
      </span>
    </div>
  );
};

// ── Hub ───────────────────────────────────────────────────────────────────────
const HubGrid = () => (
  <div style={{ padding: '28px 32px', color: 'white', maxWidth: '900px', margin: '0 auto' }}>
    <div style={{ marginBottom: '28px' }}>
      <h1 style={{ margin: '0 0 6px', fontSize: '1.6rem', letterSpacing: '2px' }}>MINIJUEGOS</h1>
      <p style={{ color: '#475569', margin: 0, fontSize: '0.9rem' }}>
        Desafíos extra para los fanáticos del fútbol argentino.
      </p>
    </div>
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
      gap: '16px',
    }}>
      {MINIJUEGOS.map(j => <TarjetaMinijuego key={j.id} juego={j} />)}
      <div style={{
        background: '#080d14', border: '1px dashed #1e293b',
        borderRadius: '16px', padding: '28px 24px',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: '8px', opacity: 0.5, minHeight: '160px',
      }}>
        <span style={{ fontSize: '1.8rem' }}></span>
        <p style={{ color: '#334155', fontSize: '0.85rem', margin: 0, textAlign: 'center' }}>
          Próximamente...
        </p>
      </div>
    </div>
  </div>
);

// ── Wrapper ───────────────────────────────────────────────────────────────────
const JuegoWrapper = ({ juego, session }) => {
  const Componente = juego.componente;
  return (
    <div>
      <div style={{
        padding: '8px 32px', background: '#080d14',
        borderBottom: '1px solid #1e293b',
        display: 'flex', alignItems: 'center', gap: '8px',
        fontSize: '0.78rem',
      }}>
        <Link to="/minijuegos" style={{ color: '#475569', textDecoration: 'none' }}>
          Minijuegos
        </Link>
        <span style={{ color: '#1e293b' }}>›</span>
        <span style={{ color: juego.color, fontWeight: 'bold' }}>{juego.titulo}</span>
      </div>
      <Componente session={session} />
    </div>
  );
};

// ── Export ────────────────────────────────────────────────────────────────────
const MinijuegosHub = ({ session }) => (
  <Routes>
    <Route index element={<HubGrid />} />
    {MINIJUEGOS.map(j => (
      <Route
        key={j.id}
        path={j.ruta}
        element={<JuegoWrapper juego={j} session={session} />}
      />
    ))}
    <Route
      path="*"
      element={
        <div style={{ padding: '60px', textAlign: 'center', color: '#64748b' }}>
          <p style={{ fontSize: '1.1rem' }}>Minijuego no encontrado.</p>
          <Link to="/minijuegos" style={{ color: '#a855f7', textDecoration: 'none' }}>
            ← Volver a minijuegos
          </Link>
        </div>
      }
    />
  </Routes>
);

export default MinijuegosHub;